import { useEffect } from 'react';

/**
 * Tawk.to live chat.
 *
 * Renders nothing. It injects Tawk's embed script, and it does so late on
 * purpose: the widget costs a few hundred kilobytes and several requests, and
 * loading it with the page would count against Largest Contentful Paint and
 * Total Blocking Time — the metrics search ranking looks at. So it waits for
 * the first sign that a person is actually here (scroll, click, key press,
 * touch) and falls back to a timer for visitors who only read.
 *
 * Effects do not run during the build-time prerender, so the script is never
 * part of the static HTML and a crawler never loads it.
 *
 * Needs both ids in the build environment; with either missing it does
 * nothing, so a local build without them is simply chat-free:
 *
 *   REACT_APP_TAWK_PROPERTY_ID   the first path segment of the embed URL
 *   REACT_APP_TAWK_WIDGET_ID     the second
 *
 * Both are public by design — they appear in the page source of every site
 * that uses Tawk — so they belong in the front-end .env.
 */
const PROPERTY_ID = process.env.REACT_APP_TAWK_PROPERTY_ID;
const WIDGET_ID = process.env.REACT_APP_TAWK_WIDGET_ID;

const FALLBACK_DELAY_MS = 8000;
const WAKE_EVENTS = ['scroll', 'pointerdown', 'keydown', 'touchstart'];

const TawkChat = () => {
  useEffect(() => {
    if (!PROPERTY_ID || !WIDGET_ID) return undefined;
    // Already added (hot reload, or the component mounting twice).
    if (document.getElementById('tawk-embed')) return undefined;

    let timer;

    const load = () => {
      WAKE_EVENTS.forEach(name => window.removeEventListener(name, load));
      clearTimeout(timer);
      if (document.getElementById('tawk-embed')) return;

      window.Tawk_API = window.Tawk_API || {};
      /*
       * Position and layering, set before the script runs. Colours, the
       * greeting and the pre-chat form live in the Tawk dashboard (Administration
       * > Channels > Chat Widget): the widget is a cross-origin iframe, so the
       * page's CSS cannot reach it.
       */
      window.Tawk_API.customStyle = {
        zIndex: 40,
        visibility: {
          desktop: { position: 'br', xOffset: 24, yOffset: 24 },
          mobile: { position: 'br', xOffset: 12, yOffset: 12 },
          bubble: { rotate: '0deg', xOffset: 0, yOffset: 0 },
        },
      };
      window.Tawk_LoadStart = new Date();

      const script = document.createElement('script');
      script.id = 'tawk-embed';
      script.async = true;
      script.src = `https://embed.tawk.to/${PROPERTY_ID}/${WIDGET_ID}`;
      script.charset = 'UTF-8';
      script.setAttribute('crossorigin', '*');
      document.body.appendChild(script);
    };

    WAKE_EVENTS.forEach(name => window.addEventListener(name, load, { once: true, passive: true }));
    timer = setTimeout(load, FALLBACK_DELAY_MS);

    return () => {
      WAKE_EVENTS.forEach(name => window.removeEventListener(name, load));
      clearTimeout(timer);
    };
  }, []);

  return null;
};

export default TawkChat;
